import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '../../lib/utils';
import { Icon } from './icon';
import { PortalContainerProvider } from '@/lib/portalContainerContext';

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-neutral-900/70 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  overlayClassName?: string;
  containerClassName?: string;
};

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      overlayClassName,
      containerClassName,
      onPointerDownOutside,
      onInteractOutside,
      ...props
    },
    ref
  ) => {
    const localRef = React.useRef<HTMLDivElement>(null);
    const [portalContainer, setPortalContainer] = React.useState<HTMLDivElement | null>(null);

    const handleContentRef = React.useCallback((node: HTMLDivElement | null) => {
      localRef.current = node;
      setPortalContainer(node);
    }, []);

    // Merge forwarded ref with local ref
    React.useImperativeHandle(ref, () => localRef.current as HTMLDivElement);

    return (
      <DialogPortal>
        <DialogOverlay className={overlayClassName} />
        <div
          className={cn(
            'fixed inset-0 z-50 flex items-start justify-center p-4 sm:items-center pointer-events-none',
            containerClassName
          )}
        >
          <DialogPrimitive.Content
            ref={handleContentRef}
            onPointerDownOutside={(event) => {
              onPointerDownOutside?.(event);
              event.preventDefault();
            }}
            onInteractOutside={(event) => {
              onInteractOutside?.(event);
              event.preventDefault();
            }}
            data-dialog-content
            className={cn(
              'pointer-events-auto flex w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-[var(--border-default)] bg-surface shadow-lg focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 duration-200 max-h-[calc(100vh-2rem)]',
              className
            )}
            {...props}
          >
            <PortalContainerProvider container={portalContainer}>
              {children}
            </PortalContainerProvider>
          </DialogPrimitive.Content>
        </div>
      </DialogPortal>
    );
  }
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ children, className }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex items-start justify-between border-b border-[var(--border-default)] bg-surface px-6 py-5',
      className
    )}
  >
    {children}
  </div>
);
DialogHeader.displayName = 'DialogHeader';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-xl font-bold uppercase tracking-[0.18em] text-brand-blue-800', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-text-secondary', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const DialogBody = ({ children, className }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex-1 min-h-0 overflow-y-auto px-6 py-6', className)}>{children}</div>
);
DialogBody.displayName = 'DialogBody';

const DialogFooter = ({ children, className }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-4', className)}>
    {children}
  </div>
);
DialogFooter.displayName = 'DialogFooter';

const DialogCloseButton = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Close
    ref={ref}
    type="button"
    className={cn(
      'inline-flex h-9 w-9 items-center justify-center rounded-md text-text-secondary transition-colors hover:bg-neutral-100 hover:text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue-500/30 focus-visible:ring-offset-2',
      className
    )}
    {...props}
  >
    {children ?? <Icon name="close" className="text-base" aria-hidden />}
    <span className="sr-only">Chiudi</span>
  </DialogPrimitive.Close>
));
DialogCloseButton.displayName = 'DialogCloseButton';

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  DialogClose,
  DialogCloseButton,
};
